import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Scale, Shield, Award, Users, Anchor, GraduationCap, Heart, ChevronRight, ExternalLink, Handshake } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const AboutPage = () => {
  // Add speakable schema for voice search
  useEffect(() => {
    const speakableSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "About Amy C. Brown | Personal Injury Attorney | Everett, WA",
      "description": "Amy C. Brown is an experienced personal injury attorney based in Everett, Washington, specializing in maritime injuries, car accidents, and personal injury cases throughout Washington State and Alaska.",
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": ["h1", "h2", ".about-intro"]
      },
      "url": "https://amycbrownlaw.com/about-amy",
      "mainEntity": {
        "@type": "Person",
        "name": "Amy C. Brown",
        "jobTitle": "Personal Injury Attorney",
        "description": "Personal injury attorney with over 10 years of experience specializing in maritime law, Jones Act claims, and personal injury cases in Washington and Alaska.",
        "telephone": "(206) 659-7177",
        "email": "amy@amycbrownlaw.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "1616 Hewitt Ave, Suite 316",
          "addressLocality": "Everett",
          "addressRegion": "WA",
          "postalCode": "98201",
          "addressCountry": "US"
        }
      }
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'about-speakable';
    script.textContent = JSON.stringify(speakableSchema);
    document.head.appendChild(script);
    
    return () => {
      document.getElementById('about-speakable')?.remove();
    };
  }, []);

  // FAQ data for About page
  const aboutFAQs = [
    {
      question: "What is Amy C. Brown's experience as a personal injury attorney?",
      answer: "Amy C. Brown has over 10 years of legal experience, including honors graduation from Seattle University School of Law and work as a federal court extern. She previously worked at a defense firm representing national clients, giving her unique insight into insurance company tactics."
    },
    {
      question: "Why does Amy's defense background help her clients?",
      answer: "Having worked at a defense firm representing insurance companies, Amy understands how opposing counsel operates, their strategies, and their tactics. This insider knowledge allows her to anticipate and counter their approaches, maximizing outcomes for her clients."
    },
    {
      question: "What areas does Amy C. Brown serve?",
      answer: "Amy serves clients throughout Washington State and Alaska, with her office based in Everett, Washington. She handles cases in Seattle, Tacoma, Bellingham, Mount Vernon, Oak Harbor, and all major cities across both states."
    },
    {
      question: "What makes Amy C. Brown different from large law firms?",
      answer: "As a solo practitioner, Amy provides direct one-on-one communication with every client. You work directly with her—not paralegals or associates—from case intake through resolution. This personal attention ensures your case receives focused, compassionate representation."
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      <SEOHead 
        title="About Amy C. Brown | Personal Injury Attorney | Everett, WA"
        description="Amy C. Brown is an experienced personal injury attorney in Everett, Washington. Former insurance defense attorney now fighting for injury victims. Over 10 years experience in maritime, car accidents, and personal injury."
        pageType="attorney"
        faqData={aboutFAQs}
      />
      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6">
              About Amy C. Brown
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Personal Injury Attorney for Cases in Washington State and Alaska
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all duration-300"
                data-testid="about-hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all duration-300"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Bio Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-8">
                Over Ten Years of Experience You Can Trust
              </h2>
              
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                After graduating with honors from Seattle University School of Law, where I served as an intern for a federal judge in the Western District of Washington, I worked at a law firm representing prominent national clients. This experience provided me with valuable insight into insurance company strategies, enabling me to build compelling cases for my clients to maximize their settlement outcomes.
              </p>

              <div className="bg-accent-soft rounded-2xl p-8 my-10 border-l-4 border-teal-500">
                <p className="text-slate-700 leading-relaxed mb-4">
                  At Amy C Brown Law, I'm more than an attorney—<strong>I've experienced trauma myself.</strong> Multiple members of my family were killed in a construction-related incident.
                </p>
                <p className="text-slate-700 leading-relaxed mb-4">
                  I vividly remember that horrible day and the days after. I know pain and I understand the pain my clients feel.
                </p>
                <p className="text-slate-700 leading-relaxed">
                  I've been a lawyer for over ten years and have guided countless people through the chaos you may be going through. <strong>I'll listen, explain your options clearly, and handle the complexities so you can heal.</strong>
                </p>
              </div>

              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                One of the most common frustrations clients express about lawyers is the lack of regular updates, leaving them in the dark about their cases. I share this concern and am deeply committed to ensuring that every client I represent is kept fully informed every step of the way, providing clear and timely communication throughout your case.
              </p>

              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                As a woman-owned law practice, some clients tell me they appreciate having a female attorney to discuss sensitive injuries or traumatic experiences. Whether that matters to you or not, my commitment remains the same: compassionate, direct representation focused entirely on your recovery and fair compensation.
              </p>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-slate-50 rounded-2xl p-6">
                <h3 className="font-heading text-xl font-semibold text-secondary mb-4">Credentials</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <GraduationCap className="w-5 h-5 text-teal-600 mt-0.5" />
                    <span className="text-slate-600 text-sm">Seattle University School of Law (Honors)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Scale className="w-5 h-5 text-teal-600 mt-0.5" />
                    <span className="text-slate-600 text-sm">Federal Court Extern, Western District of Washington</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Award className="w-5 h-5 text-teal-600 mt-0.5" />
                    <span className="text-slate-600 text-sm">10+ Years of Experience</span>
                  </li>
                </ul>
                
                {/* Award Badges */}
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h4 className="font-heading text-sm font-semibold text-secondary mb-4">Awards & Recognition</h4>
                  <div className="flex flex-col items-center gap-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_492a0bf8-a316-49a1-8dbd-117f79877488/artifacts/aecnwanu_10Best.png"
                      alt="10 Best Personal Injury Attorney 2025 Award - American Institute of Personal Injury Attorneys Client Satisfaction"
                      title="Amy C. Brown - 10 Best Attorney Award"
                      loading="lazy"
                      className="h-24 w-auto"
                      data-testid="about-award-10best"
                    />
                    <img 
                      src="https://customer-assets.emergentagent.com/job_492a0bf8-a316-49a1-8dbd-117f79877488/artifacts/2s3q26fe_top10.webp"
                      alt="Top 10 Personal Injury Attorney 2025 Award - Attorney and Practice Magazine Washington State"
                      title="Top 10 Personal Injury Lawyer Washington 2025"
                      loading="lazy"
                      className="h-24 w-auto"
                      data-testid="about-award-top10"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-[#4A9B9B] rounded-2xl p-6 text-white">
                <h3 className="font-heading text-xl font-semibold mb-4">Why Choose Me</h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-white/70">✓</span>
                    Expertise in insurance tactics
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-white/70">✓</span>
                    Skilled in developing strategic approaches
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-white/70">✓</span>
                    No fees unless you win
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-white/70">✓</span>
                    <Link to="/woman-personal-injury-lawyer" className="hover:underline">Woman owned</Link>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-white/70">✓</span>
                    I sincerely want to help people
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three Pillars */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover-lift">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <Scale className="w-7 h-7 text-teal-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                Unique Perspective from Both Sides
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Having started my career defending employers, shipowners, and insurance companies, I gained a rare understanding of their strategies, which I now use to effectively represent plaintiffs, ensuring fair treatment for those injured at sea or on land.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover-lift">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-teal-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                Personalized Support as a Solo Practicing Attorney
              </h3>
              <p className="text-slate-600 leading-relaxed">
                As a solo practitioner, I offer direct, one-on-one communication, answering every call and email myself to keep clients informed and supported, eliminating the frustration of navigating large firm hierarchies.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover-lift">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <Anchor className="w-7 h-7 text-teal-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                From Sea to Courtroom
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Growing up in the Puget Sound and working on vessels in Southeast Alaska and Mexico's Sea of Cortez, I developed a deep connection to the maritime world, which I now channel into my legal practice to advocate for seamen and people involved in car crashes and other types of injury. I help ensure their rights are protected.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Solo Attorney */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-heading text-3xl font-bold text-secondary mb-6">
                Why Choose a Solo Attorney?
              </h2>
              <h3 className="text-lg text-teal-600 font-medium mb-4">
                Benefits of Choosing a Small Firm
              </h3>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-teal-500" />
                  <span className="text-slate-700 font-medium">Work Directly with Me</span>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-teal-500" />
                  <span className="text-slate-700 font-medium">I Work Your Entire Case</span>
                </div>
                <div className="flex items-center gap-3">
                  <Heart className="w-5 h-5 text-teal-500" />
                  <span className="text-slate-700 font-medium">Compassionate and Strategic</span>
                </div>
              </div>

              <p className="text-slate-600 leading-relaxed mb-4">
                The distinct advantage of choosing a small firm like mine lies in the personal attention you will experience. Large firms frequently delegate the majority of legal work to paralegals, and when you call, it is often a paralegal or assistant who responds, with the attorney remaining less involved.
              </p>
              <p className="text-slate-600 leading-relaxed">
                With my firm, you work directly with me. I am personally committed to handling your case, negotiating with insurance companies, and collaborating with medical providers on your behalf. The stress of navigating a large firm can be as overwhelming as the injury itself, and I recognize the emotional burden you are already enduring. What you need is a calm, compassionate, strategic approach—that's how I handle cases.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-3xl font-bold text-secondary mb-6">
                Why My Defense Experience Matters
              </h2>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <Scale className="w-5 h-5 text-teal-500" />
                  <span className="text-slate-700 font-medium">Strategic Defense Insight</span>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-teal-500" />
                  <span className="text-slate-700 font-medium">Skilled Evidence Handling</span>
                </div>
                <div className="flex items-center gap-3">
                  <Award className="w-5 h-5 text-teal-500" />
                  <span className="text-slate-700 font-medium">Maximized Case Outcomes</span>
                </div>
              </div>

              <p className="text-slate-600 leading-relaxed mb-4">
                Choosing a personal injury attorney with defense experience can significantly influence your case's success. My background at a defense firm provides a unique perspective, equipping me with strategic defense insight into how opposing counsel operates, allowing me to anticipate and counter their tactics effectively.
              </p>
              <p className="text-slate-600 leading-relaxed">
                I am skilled in evidence handling, adept at challenging claims and identifying weaknesses in the opposition's case. This expertise enables me to craft robust strategies, ensuring maximized case outcomes and enhancing your chances of a favorable resolution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-20 bg-slate-50" data-testid="values-section">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Heart className="w-4 h-4" />
              Our Values
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              What I Stand For
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              These principles guide every case I take and every client I serve.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-heading font-bold text-secondary mb-2">Compassion First</h3>
              <p className="text-slate-600 text-sm">
                I've experienced personal loss from tragedy. I understand the pain you're going through and treat every client with genuine empathy.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-teal-700" />
              </div>
              <h3 className="font-heading font-bold text-secondary mb-2">Direct Access</h3>
              <p className="text-slate-600 text-sm">
                When you call, you talk to me—not a paralegal. I personally handle your case from start to finish because you deserve that attention.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Scale className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-heading font-bold text-secondary mb-2">No Fee Unless You Win</h3>
              <p className="text-slate-600 text-sm">
                Justice shouldn't depend on your bank account. I work on contingency because I believe in your case—you pay nothing unless we recover.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-teal-700" />
              </div>
              <h3 className="font-heading font-bold text-secondary mb-2">Honest Guidance</h3>
              <p className="text-slate-600 text-sm">
                I'll tell you the truth about your case, even if it's not what you want to hear. You deserve honesty, not false promises.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Community Involvement Section */}
      <section className="py-20 bg-white" data-testid="community-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Handshake className="w-4 h-4" />
              Community Involvement
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              Giving Back to the Community
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              I believe in supporting the communities I serve in Everett, Western Washington, and beyond. My volunteer work and affiliations reflect my commitment to helping people—whether through legal advocacy, first responder support, or local causes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Professional Associations */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
              <h3 className="font-heading text-lg font-bold text-secondary mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5 text-teal-600" />
                Professional Associations
              </h3>
              <ul className="space-y-3">
                <li>
                  <a 
                    href="https://www.wsba.org/Legal-Community/Sections/Solo-and-Small-Practice-Section" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-slate-700 hover:text-teal-600 transition-colors group"
                  >
                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-teal-600" />
                    <span>WSBA Solo & Small Practice Section</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.wsba.org/legal-community/sections/dispute-resolution-section" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-slate-700 hover:text-teal-600 transition-colors group"
                  >
                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-teal-600" />
                    <span>WSBA Dispute Resolution Section</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.wwl.org/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-slate-700 hover:text-teal-600 transition-colors group"
                    data-testid="about-wwl-link"
                  >
                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-teal-600" />
                    <span>Washington Women Lawyers (WWL)</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.bloswa.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-slate-700 hover:text-teal-600 transition-colors group"
                  >
                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-teal-600" />
                    <span>Business Ladies of Society</span>
                  </a>
                </li>
              </ul>
            </div>

            {/* Business Networks */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
              <h3 className="font-heading text-lg font-bold text-secondary mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-teal-600" />
                Business Networks
              </h3>
              <ul className="space-y-3">
                <li>
                  <a 
                    href="https://womenbusinessowners.org/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-slate-700 hover:text-teal-600 transition-colors group"
                  >
                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-teal-600" />
                    <span>Women Business Owners</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.snohomishnetworkingwomen.org/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-slate-700 hover:text-teal-600 transition-colors group"
                  >
                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-teal-600" />
                    <span>Snohomish Networking Women</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.provisors.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-slate-700 hover:text-teal-600 transition-colors group"
                  >
                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-teal-600" />
                    <span>Provisors</span>
                  </a>
                </li>
              </ul>
            </div>

            {/* Volunteer Work */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
              <h3 className="font-heading text-lg font-bold text-secondary mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5 text-primary" />
                Pro Bono & Volunteer Work
              </h3>
              <ul className="space-y-3">
                <li>
                  <a 
                    href="https://snocolegal.org/legal-help/#wills" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-slate-700 hover:text-teal-600 transition-colors group"
                  >
                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-teal-600" />
                    <span>Snohomish County Legal Services</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.willclinic.org/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-slate-700 hover:text-teal-600 transition-colors group"
                  >
                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-teal-600" />
                    <span>Will Clinic</span>
                  </a>
                </li>
                <li className="flex items-center gap-2 text-slate-700">
                  <Shield className="w-4 h-4 text-primary" />
                  <span>Low-Cost Wills for First Responders</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Call Now For a Free Case Evaluation
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            I personally respond to every inquiry within 1 business day.
          </p>
          <a 
            href="tel:425-675-1224"
            className="inline-flex items-center gap-3 bg-white text-teal-700 px-10 py-5 rounded-full font-bold text-xl hover:bg-teal-50 transition-all duration-300 shadow-xl"
            data-testid="about-cta-phone"
          >
            <Phone className="w-6 h-6" />
            (425) 675-1224
          </a>
        </div>
      </section>

      {/* Areas of Legal Expertise - Entity Links */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              Areas of Legal Expertise
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              I specialize in these areas of law, bringing over a decade of experience to every case.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
              <h3 className="font-heading font-semibold text-secondary mb-2">
                <a href="https://en.wikipedia.org/wiki/Personal_injury" target="_blank" rel="noopener noreferrer" className="hover:text-teal-600 transition-colors inline-flex items-center gap-1">
                  Personal Injury Law
                  <ExternalLink className="w-4 h-4" />
                </a>
              </h3>
              <p className="text-slate-600 text-sm">Representing individuals who have been physically or psychologically injured due to negligence or wrongdoing of another party.</p>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
              <h3 className="font-heading font-semibold text-secondary mb-2">
                <a href="https://en.wikipedia.org/wiki/Admiralty_law" target="_blank" rel="noopener noreferrer" className="hover:text-teal-600 transition-colors inline-flex items-center gap-1">
                  Maritime & Admiralty Law
                  <ExternalLink className="w-4 h-4" />
                </a>
              </h3>
              <p className="text-slate-600 text-sm">Specialized area of law governing nautical issues and maritime activities, including injuries to seamen and maritime workers.</p>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
              <h3 className="font-heading font-semibold text-secondary mb-2">
                <a href="https://en.wikipedia.org/wiki/Merchant_Marine_Act_of_1920" target="_blank" rel="noopener noreferrer" className="hover:text-teal-600 transition-colors inline-flex items-center gap-1">
                  Jones Act Claims
                  <ExternalLink className="w-4 h-4" />
                </a>
              </h3>
              <p className="text-slate-600 text-sm">Federal statute providing protections for injured seamen, allowing them to seek compensation from employers for negligence.</p>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
              <h3 className="font-heading font-semibold text-secondary mb-2">
                <a href="https://en.wikipedia.org/wiki/Wrongful_death_claim" target="_blank" rel="noopener noreferrer" className="hover:text-teal-600 transition-colors inline-flex items-center gap-1">
                  Wrongful Death
                  <ExternalLink className="w-4 h-4" />
                </a>
              </h3>
              <p className="text-slate-600 text-sm">Claims brought when a person dies due to the legal fault of another person or entity, seeking compensation for surviving family members.</p>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
              <h3 className="font-heading font-semibold text-secondary mb-2">
                <a href="https://en.wikipedia.org/wiki/Premises_liability" target="_blank" rel="noopener noreferrer" className="hover:text-teal-600 transition-colors inline-flex items-center gap-1">
                  Premises Liability
                  <ExternalLink className="w-4 h-4" />
                </a>
              </h3>
              <p className="text-slate-600 text-sm">Legal responsibility of property owners for injuries occurring on their property due to unsafe conditions.</p>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
              <h3 className="font-heading font-semibold text-secondary mb-2">
                <a href="https://en.wikipedia.org/wiki/Contingent_fee" target="_blank" rel="noopener noreferrer" className="hover:text-teal-600 transition-colors inline-flex items-center gap-1">
                  Contingency Fee Representation
                  <ExternalLink className="w-4 h-4" />
                </a>
              </h3>
              <p className="text-slate-600 text-sm">I work on a contingent fee basis—you pay nothing unless I win your case, making quality legal representation accessible to everyone.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Practice Areas Links */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl font-bold text-secondary text-center mb-12">
            How I Can Help You
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Maritime Injuries', path: '/maritime-injuries', icon: Anchor },
              { title: 'Car Crashes', path: '/car-crashes', icon: Shield },
              { title: 'First Responder Crashes', path: '/first-responder-crashes', icon: Shield },
              { title: 'Motorcycle Crashes', path: '/motorcycle-crashes', icon: Shield },
              { title: 'Slip and Falls', path: '/slip-and-falls', icon: Shield },
            ].map((area) => (
              <Link 
                key={area.path}
                to={area.path}
                className="flex items-center gap-4 bg-white rounded-xl p-6 shadow-md hover-lift border border-slate-100 group"
              >
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center group-hover:bg-teal-500 transition-colors">
                  <area.icon className="w-6 h-6 text-teal-600 group-hover:text-white transition-colors" />
                </div>
                <span className="font-medium text-slate-700 group-hover:text-teal-600 transition-colors">{area.title}</span>
                <ChevronRight className="w-5 h-5 text-slate-400 ml-auto group-hover:text-teal-600 transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
